import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { alertMessages, AlertService } from '../../../shared/services/alert.service';
import {
  ProfileTypeUtils,
  ProfileLookingForUtils,
  // profileTypeToTargetGroup, // TODO: re-enable when accountTargetGroups sync is approved
} from '../../../shared/utils/student-profile.utils';
import { catchError, finalize /*, switchMap */ } from 'rxjs/operators';
import { of } from 'rxjs';
import { ModelStudentprofile } from 'src/app/model/model.studentprofile';
import { StudentProfileService } from 'src/app/api/services/student-profile.service';
import { StudentProfile } from 'src/app/api/models/student-profile';
import { StudentProfileType } from 'src/app/api/models/student-profile-type';
import { CurrentUserStore } from 'src/app/shared/stores/current-user.store';
// import { UserService } from 'src/app/shared/services/user.service'; // TODO: re-enable when accountTargetGroups sync is approved

@Component({
  selector: 'app-modal-student-profile-status',
  templateUrl: './modal-student-profile-status.component.html',
  styleUrls: ['./modal-student-profile-status.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslateModule],
})
export class ModalStudentProfileStatusComponent implements OnInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly alertService = inject(AlertService);
  private readonly studentProfileService = inject(StudentProfileService);
  private readonly currentUserStore = inject(CurrentUserStore);
  // TODO: re-enable when accountTargetGroups sync via the status modal is approved
  // private readonly userService = inject(UserService);

  data!: ModelStudentprofile;

  readonly profileStatus = signal<
    Pick<ModelStudentprofile, 'id' | 'profileType' | 'lookingFor'>
  >({
    id: '',
    profileType: '',
    lookingFor: '',
  });
  readonly isFormValid = computed(() => {
    const status = this.profileStatus();
    return !!status.id && !!status.profileType && !!status.lookingFor;
  });

  readonly isLoading = signal(false);
  readonly hasError = signal(false);

  readonly profileTypes = ProfileTypeUtils.getOptions();
  readonly lookingForOptions = ProfileLookingForUtils.getOptions();

  ngOnInit(): void {
    if (this.data) {
      this.profileStatus.set({
        id: this.data.id || '',
        profileType: this.data.profileType || '',
        lookingFor: this.data.lookingFor || '',
      });
    }
  }

  updateField(field: keyof ModelStudentprofile, value: string): void {
    this.profileStatus.update((status) => ({ ...status, [field]: value }));
  }

  trackByValue(_index: number, item: { value: string }): string {
    return item.value;
  }

  async save(): Promise<void> {
    if (!this.isFormValid()) return;

    this.isLoading.set(true);

    const updateData: ModelStudentprofile = {
      ...this.data,
      ...this.profileStatus(),
    };

    // TODO(#20302): Sync accountTargetGroups to the new profileType when this
    // becomes the intended behaviour. The implementation below was prepared in
    // mirror of the registration-time mapping (pages/landing/register.component.ts)
    // but is intentionally disabled — product wants to revisit before shipping.
    //
    // const targetGroup = profileTypeToTargetGroup(updateData.profileType);
    //
    // this.studentProfileService
    //   .updateStudentProfile({
    //     id: updateData.id!,
    //     body: updateData as unknown as StudentProfile,
    //   })
    //   .pipe(
    //     switchMap((profileResult) =>
    //       this.userService.apiGetAccountCurrent().pipe(
    //         switchMap((account: any) => {
    //           if (!account?.id) {
    //             return of(profileResult);
    //           }
    //           const updatedAccount = {
    //             ...account,
    //             accountTargetGroups: [targetGroup],
    //           };
    //           return this.userService
    //             .apiUpdateAccountCurrent(account.id, updatedAccount)
    //             .pipe(
    //               catchError((error) => {
    //                 // Don't fail the whole save if account update errors -
    //                 // the profile is already persisted. Report so it gets
    //                 // picked up, then continue.
    //                 this.alertService.errorWithReport(
    //                   error,
    //                   'studentProfile:syncAccountTargetGroups',
    //                 );
    //                 return of(profileResult);
    //               }),
    //             );
    //         }),
    //       ),
    //     ),
    //     ...
    //   )

    this.studentProfileService
      .updateStudentProfile({
        id: updateData.id!,
        body: updateData as unknown as StudentProfile,
      })
      .pipe(
        catchError((error) => {
          this.alertService.errorWithReport(error, 'studentProfile:updateStatus');
          return of(false as const);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (result) => {
          if (result !== false) {
            // Sync the store so capability-driven UI (nav, *appHasCapability)
            // updates immediately without a page refresh.
            this.currentUserStore.setProfileType(
              (updateData.profileType as StudentProfileType) || null,
            );
            this.alertService.success(alertMessages.successDataSaved);
            this.closeModal(true);
          }
        },
      });
  }

  closeModal(result: boolean = false): void {
    this.activeModal.close(result);
  }
}
